import { isEmptyValue, validateFormValues } from './form-validation.js';
import { fieldWithRole, NON_DATA_FIELD_TYPES, optionLabel, projectScopeFields, visitScopeFields } from './forms.js';
/**
 * A trip is one visit to one branch that covers several projects: the main
 * visit carries the branch-level answers and its own project block; each
 * additional project visit carries only a project block. One trip is one
 * submission and one Zoho ticket.
 *
 * Everything here is pure and shared by the server (authoritative), the phone
 * and the form builder's preview, so all three apply the same rules.
 */
// ---------------------------------------------------------------------------
// Dependent fields
// ---------------------------------------------------------------------------
/** The option of `field` with the same key as the source's current value, if there is one. */
function prefillValue(field, source) {
    return typeof source === 'string' && (field.options ?? []).some((o) => o.value === source) ? source : undefined;
}
/**
 * Applies one answer to a block's values with the dependent-field rules:
 *
 * - Changing a parent clears every field that depends on it (and, in turn,
 *   their dependents), so a child never keeps a value its new parent does not offer.
 * - A field auto-filled from the changed one (issue type from service) follows
 *   it, unless the agent overrode it: an overridden value is kept, and
 *   `prefillMismatch` reports it so the screen can say so. Nothing is asked.
 *
 * Values of hidden fields are kept (they are dropped at submission), so turning
 * a toggle off and on again does not lose what was typed.
 */
export function applyFieldChange(fields, values, fieldKey, value) {
    const next = { ...values, [fieldKey]: value };
    const previous = values;
    const queue = [fieldKey];
    const seen = new Set();
    while (queue.length > 0) {
        const changed = queue.shift();
        if (seen.has(changed))
            continue;
        seen.add(changed);
        for (const field of fields) {
            if (field.fieldKey === fieldKey)
                continue;
            if (field.dependsOn?.fieldKey === changed && next[field.fieldKey] !== undefined) {
                delete next[field.fieldKey];
                queue.push(field.fieldKey);
            }
            if (field.prefillFrom === changed) {
                const current = next[field.fieldKey];
                // Following = empty, or still what the source filled in before this change.
                const following = isEmptyValue(current) || current === prefillValue(field, previous[changed]);
                if (!following)
                    continue;
                const filled = prefillValue(field, next[changed]);
                if (filled === current)
                    continue;
                if (filled === undefined)
                    delete next[field.fieldKey];
                else
                    next[field.fieldKey] = filled;
                queue.push(field.fieldKey);
            }
        }
    }
    return next;
}
/**
 * When an auto-filled field was changed by the agent and no longer matches its
 * source (e.g. the issue belongs to another service than the one chosen), the
 * source's value that would have been filled; null otherwise.
 */
export function prefillMismatch(fields, field, values) {
    if (!field.prefillFrom)
        return null;
    const current = values[field.fieldKey];
    if (isEmptyValue(current))
        return null;
    const source = fields.find((f) => f.fieldKey === field.prefillFrom);
    if (!source)
        return null;
    const expected = prefillValue(field, values[source.fieldKey]);
    return expected !== undefined && expected !== current ? expected : null;
}
/** The project of one block: the chosen option of the Project-role field, or the name typed for it. */
export function blockProject(fields, values, typed, language = 'ar') {
    const field = fieldWithRole(fields, 'PROJECT');
    if (!field)
        return { key: null, name: null, typed: false };
    const value = values[field.fieldKey];
    const option = typeof value === 'string' ? (field.options ?? []).find((o) => o.value === value) : undefined;
    if (option)
        return { key: option.value, name: optionLabel(option, language), typed: false };
    const entry = (typed ?? []).find((e) => e.fieldKey === field.fieldKey);
    return entry ? { key: null, name: entry.name, typed: true } : { key: null, name: null, typed: false };
}
/** Human label of a stored answer, for descriptions and tables. Keys stay keys when the option is gone. */
export function describeValue(field, value, language = 'ar') {
    if (value === undefined || value === null || value === '')
        return null;
    const label = (key) => {
        const option = (field.options ?? []).find((o) => o.value === key);
        return option ? optionLabel(option, language) : key;
    };
    if (Array.isArray(value))
        return value.length > 0 ? value.map(label).join(language.startsWith('en') ? ', ' : '، ') : null;
    if (typeof value === 'boolean')
        return language.startsWith('en') ? (value ? 'Yes' : 'No') : value ? 'نعم' : 'لا';
    if (field.fieldType === 'SELECT' && typeof value === 'string')
        return label(value);
    return String(value);
}
/**
 * Validates the main visit (every field) and each additional project visit (its
 * per-project fields, evaluated next to the trip's branch-level answers, since a
 * project field may depend on one). Errors say which block they belong to.
 */
export function validateTrip(fields, input) {
    const scopeOf = new Map(fields.map((f) => [f.fieldKey, f.scope]));
    const main = validateFormValues(fields, input.main.formData, {
        attachmentCounts: input.main.attachmentCounts,
        manualOptionCounts: input.main.manualOptionCounts,
        hasBrand: input.main.hasBrand,
        hasBranch: input.main.hasBranch,
        rejectUnknownFields: input.rejectUnknownFields,
    });
    const errors = main.errors.map((e) => ({ ...e, block: scopeOf.get(e.fieldKey) === 'PROJECT' ? 0 : null }));
    const branchLevel = {};
    for (const field of visitScopeFields(fields)) {
        if (field.fieldKey in main.values)
            branchLevel[field.fieldKey] = main.values[field.fieldKey];
    }
    const perProject = projectScopeFields(fields);
    // Validated per block: every per-project field, photos included (their counts come from the manifest).
    const projectKeys = new Set(perProject.map((f) => f.fieldKey));
    // Carried in a block's formData: per-project fields that hold a value.
    const dataKeys = new Set(perProject.filter((f) => !NON_DATA_FIELD_TYPES.includes(f.fieldType)).map((f) => f.fieldKey));
    const additionalValues = input.additional.map((block, index) => {
        const own = {};
        for (const [key, value] of Object.entries(block.formData))
            if (dataKeys.has(key))
                own[key] = value;
        const result = validateFormValues(fields, { ...branchLevel, ...own }, {
            fieldKeys: projectKeys,
            attachmentCounts: block.attachmentCounts,
            manualOptionCounts: block.manualOptionCounts,
        });
        if (input.rejectUnknownFields) {
            for (const key of Object.keys(block.formData)) {
                if (!dataKeys.has(key)) {
                    errors.push({
                        fieldKey: key,
                        code: 'UNKNOWN_FIELD',
                        message: `"${key}" is not a per-project field of this form version`,
                        block: index + 1,
                    });
                }
            }
        }
        errors.push(...result.errors.map((e) => ({ ...e, block: index + 1 })));
        return result.values;
    });
    return { errors, mainValues: main.values, additionalValues };
}
/** Projects chosen more than once in the same trip (a warning, never a block). Keys are option keys or typed names. */
export function repeatedProjects(projects) {
    const seen = new Map();
    for (const project of projects) {
        if (!project.name)
            continue;
        const id = project.key ?? `typed:${project.name.trim().toLowerCase()}`;
        seen.set(id, (seen.get(id) ?? 0) + 1);
    }
    const names = [];
    for (const project of projects) {
        const id = project.key ?? `typed:${project.name?.trim().toLowerCase()}`;
        if (project.name && (seen.get(id) ?? 0) > 1 && !names.includes(project.name))
            names.push(project.name);
    }
    return names;
}
// ---------------------------------------------------------------------------
// Photo file names on the Zoho ticket
// ---------------------------------------------------------------------------
/**
 * Characters a file name cannot hold on common file systems or in a multipart
 * header, plus control and bidi-override characters (an invisible RLO could
 * make "gpj.exe" display as "exe.jpg").
 */
const PATH_UNSAFE_CHARS = /[/\\:*?"<>|]/g;
/** Control characters (C0, DEL) and the bidi marks, overrides and isolates. */
function isInvisibleOrControl(codePoint) {
    return (codePoint <= 0x1f ||
        codePoint === 0x7f ||
        codePoint === 0x200e ||
        codePoint === 0x200f ||
        (codePoint >= 0x202a && codePoint <= 0x202e) ||
        (codePoint >= 0x2066 && codePoint <= 0x2069));
}
/**
 * One segment of a file name: Arabic and Latin letters are kept as they are;
 * path-unsafe characters become spaces, whitespace is collapsed, and leading or
 * trailing dots and spaces are removed. Empty -> `fallback`.
 */
export function safeFileNamePart(text, fallback, maxLength = 60) {
    const normalized = text
        .normalize('NFC')
        .replace(PATH_UNSAFE_CHARS, ' ')
        .replace(/./gsu, (ch) => (isInvisibleOrControl(ch.codePointAt(0)) ? ' ' : ch))
        .replace(/\s+/g, ' ')
        .trim();
    // Truncate on code points, not UTF-16 units: a slice through a surrogate pair
    // would leave a lone surrogate that breaks UTF-8 encoding of the file name.
    const cleaned = [...normalized]
        .slice(0, maxLength)
        .join('')
        .replace(/^[.\s-]+|[.\s]+$/g, '');
    return cleaned || fallback;
}
/**
 * File names for a trip's photos, in manifest order:
 * `<project>-<photo field key>-<n>.<ext>`, e.g. `Cybex-receipt-1.jpg` or
 * `مصر للطيران-receipt-2.jpg`.
 *
 * `n` counts per project name and photo field across the whole trip (1, 2, …
 * in the order the photos were taken), so two blocks for the same project, or
 * two projects whose names clean up to the same text, never collide. The names
 * are computed once, when the trip is received, and stored: retries and
 * re-uploads always use the same name.
 */
export function tripPhotoFileNames(photos, projectNameOf) {
    const counters = new Map();
    const used = new Set();
    return photos.map((photo) => {
        const project = safeFileNamePart(projectNameOf(photo.block) ?? '', photo.block === 0 ? 'project' : `project ${photo.block + 1}`);
        const extension = photo.mime === 'image/png' ? 'png' : 'jpg';
        const counterKey = `${project.toLowerCase()}\u0000${photo.fieldKey}`;
        let n = counters.get(counterKey) ?? 0;
        let name;
        do {
            n += 1;
            name = `${project}-${photo.fieldKey}-${n}.${extension}`;
        } while (used.has(name.toLowerCase()));
        counters.set(counterKey, n);
        used.add(name.toLowerCase());
        return name;
    });
}
//# sourceMappingURL=trip.js.map